/**
 * Packaged-SKU price suggestions from Guide targets (RRP, Progressive retail, O.P.).
 */

import { isKegSku, isPackSku, isLooseSku } from 'util/pricing-fields';
import {
  getUnitCostExGstForField,
  resolveTargetEntry,
  parseCaseUnits,
  GST_MULT,
} from './marginUtils';
import { applyRounding, targetPctToDivisor } from './kegPricingSuggestions';

export const SLAB_CASE_DEFAULTS = {
  slab_case_discount_large: 0.10,
  slab_case_discount_small: 0.075,
  slab_case_large_units: 24,
};

function parseRuleFrac(raw, fallback) {
  const n = parseFloat(raw);
  if (Number.isNaN(n) || n < 0 || n >= 1) return fallback;
  return n;
}

function parseRuleCount(raw, fallback) {
  const n = parseFloat(raw);
  if (Number.isNaN(n) || n <= 0) return fallback;
  return n;
}

/** Shopify slab discount: 24-unit (or threshold) slabs vs smaller. */
export function slabCaseDiscount(totalUnits, rules) {
  const threshold = parseRuleCount(
    rules?.slab_case_large_units,
    SLAB_CASE_DEFAULTS.slab_case_large_units,
  );
  const large = parseRuleFrac(
    rules?.slab_case_discount_large,
    SLAB_CASE_DEFAULTS.slab_case_discount_large,
  );
  const small = parseRuleFrac(
    rules?.slab_case_discount_small,
    SLAB_CASE_DEFAULTS.slab_case_discount_small,
  );
  return totalUnits >= threshold ? large : small;
}

export function slabCaseFromUnitRrp(sku, perUnitRrp, rules) {
  const parsed = parseCaseUnits(sku?.caseType ?? sku?.case_type);
  const unit = parseFloat(perUnitRrp);
  if (!parsed || parsed.totalUnits <= 0 || Number.isNaN(unit) || unit <= 0) {
    return null;
  }
  const disc = slabCaseDiscount(parsed.totalUnits, rules);
  return applyRounding(unit * parsed.totalUnits * (1 - disc), 'nearest_50');
}

export function unitRrpFromSlabCase(sku, rrpCase, rules) {
  const parsed = parseCaseUnits(sku?.caseType ?? sku?.case_type);
  const sell = parseFloat(rrpCase);
  if (!parsed || parsed.totalUnits <= 0 || Number.isNaN(sell) || sell <= 0) {
    return null;
  }
  const disc = slabCaseDiscount(parsed.totalUnits, rules);
  const keep = 1 - disc;
  if (keep <= 0) return null;
  return sell / (parsed.totalUnits * keep);
}

function parsePrice(v) {
  if (v == null || v === '') return NaN;
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? NaN : n;
}

function skuRange(sku) {
  return sku?.productRange ?? sku?.product_range ?? null;
}

function sellFromCost(costExGst, targetPct, rounding) {
  const d = targetPctToDivisor(targetPct);
  if (d == null || d <= 0 || costExGst == null || Number.isNaN(costExGst) || costExGst <= 0) {
    return null;
  }
  return applyRounding((costExGst / d) * GST_MULT, rounding || 'ceil_50');
}

/**
 * Suggested RRP Unit (loose) / RRP Pack (pack) from Off Prem + Guide GP%.
 * RRP Case = per-unit RRP × case units × (1 − slab discount). Slab wins over GP target.
 * @returns {{ rrpUnit: number|null, rrpPack: number|null, rrpCase: number|null }}
 */
export function calculateRrpSuggestions(sku, prices, targetMap, rules) {
  const out = { rrpUnit: null, rrpPack: null, rrpCase: null };
  if (!sku || isKegSku(sku)) return out;
  const range = skuRange(sku);
  if (isLooseSku(sku)) {
    const cost = getUnitCostExGstForField(sku, 'rrpUnit', prices);
    const entry = resolveTargetEntry(targetMap, range, 'rrpUnit', '');
    out.rrpUnit = sellFromCost(cost, entry?.pct, entry?.rounding || 'ceil_50');
  }
  if (isPackSku(sku)) {
    const cost = getUnitCostExGstForField(sku, 'rrpPack', prices);
    const entry = resolveTargetEntry(targetMap, range, 'rrpPack', '');
    out.rrpPack = sellFromCost(cost, entry?.pct, entry?.rounding || 'ceil_50');
  }
  const fromUnit = parsePrice(out.rrpUnit ?? prices?.rrpUnit);
  let perUnit = !Number.isNaN(fromUnit) && fromUnit > 0 ? fromUnit : null;
  if (perUnit == null) {
    const pack = parsePrice(out.rrpPack ?? prices?.rrpPack);
    const parsed = parseCaseUnits(sku?.caseType ?? sku?.case_type);
    if (!Number.isNaN(pack) && pack > 0 && parsed?.unitsPerPack > 0) {
      perUnit = pack / parsed.unitsPerPack;
    }
  }
  out.rrpCase = slabCaseFromUnitRrp(sku, perUnit, rules);
  return out;
}

/**
 * Progressive retail suggestions (not the trade price_progressive field).
 * Pack: 25% GP, nearest cent. Unit: RRP Unit × markup, nearest 50c.
 */
export function calculateProgressiveRetailSuggestions(sku, prices, targetMap, rules) {
  const out = { progressiveUnit: null, progressivePack: null };
  if (!sku || isKegSku(sku)) return out;
  const range = skuRange(sku);
  // Accept 0.0265 (fraction), 2.65 (percent), or 1.0265 (multiplier).
  const rawMarkup = parseFloat(rules?.progressive_unit_markup ?? 0.0265);
  let progressiveMult = 1.0265;
  if (!Number.isNaN(rawMarkup) && rawMarkup > 0) {
    if (rawMarkup > 1 && rawMarkup < 1.5) progressiveMult = rawMarkup;
    else if (rawMarkup >= 1.5) progressiveMult = 1 + rawMarkup / 100;
    else progressiveMult = 1 + rawMarkup;
  }

  const rrpUnit = parsePrice(prices?.rrpUnit);
  if (!Number.isNaN(rrpUnit) && rrpUnit > 0) {
    out.progressiveUnit = applyRounding(rrpUnit * progressiveMult, 'nearest_50');
  }

  if (isPackSku(sku)) {
    const cost = getUnitCostExGstForField(sku, 'rrpPack', prices);
    const entry = resolveTargetEntry(targetMap, range, 'rrpPack', '');
    const pct = entry?.pct != null ? entry.pct : 25;
    out.progressivePack = sellFromCost(cost, pct, 'nearest_cent');
  }
  return out;
}

/**
 * In-house can pour (O.P. / price_bar). Monday ✨ O.P divisors.
 */
export function calculateOpSuggestion(sku, prices, rules) {
  if (!sku || isKegSku(sku) || !isLooseSku(sku)) return null;
  const cost = getUnitCostExGstForField(sku, 'rrpUnit', prices);
  if (cost == null || Number.isNaN(cost) || cost <= 0) return null;
  const unit = String(sku.unitType ?? sku.unit_type ?? '');
  const type = String(sku.productType ?? sku.product_type ?? '');
  let divisor = parseFloat(rules?.op_divisor_330 ?? 0.32);
  if (unit === '440' || unit === '440ml') {
    divisor = parseFloat(rules?.op_divisor_440 ?? 0.45);
  }
  if (type === 'Wild' || type === 'Clean Barrel' || type === 'Other') {
    divisor = parseFloat(rules?.op_divisor_specialty ?? 0.50);
  }
  if (!divisor || divisor <= 0) return null;
  return applyRounding((cost / divisor) * GST_MULT, 'nearest_50');
}
